class KoketaSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sliderReady === 'true') return;

    this.dataset.sliderReady = 'true';
    this.track = this.querySelector('[data-slider-track]');
    this.slides = [...this.querySelectorAll('[data-slider-slide]')];
    this.delay = Number(this.dataset.delay || 5000);
    this.index = 0;
    this.isVisible = false;
    this.ready = false;
    this.resumeTimer = 0;
    this.scrollFrame = 0;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.handlePointerEnter = () => {
      if (this.canHover.matches) this.pause();
    };
    this.handlePointerLeave = () => {
      if (this.canHover.matches) this.play();
    };
    this.handlePointerDown = () => this.pause();
    this.handlePointerUp = () => this.resumeAfterInteraction();
    this.handleFocusIn = () => this.pause();
    this.handleFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.play();
    };
    this.handleTrackScroll = () => {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => this.updateIndexFromScroll());
    };

    this.addEventListener('pointerenter', this.handlePointerEnter);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.track?.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
    this.track?.addEventListener('pointerup', this.handlePointerUp, { passive: true });
    this.track?.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
    this.track?.addEventListener('scroll', this.handleTrackScroll, { passive: true });
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('visibilitychange', this.handleVisibility);

    // Read the browser-restored horizontal position after layout settles. This
    // never writes scrollLeft, so the section cannot jump when the page loads.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.updateIndexFromScroll();
        this.ready = true;
        if (this.isVisible) this.play();
      });
    });

    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.2);
        this.isVisible ? this.play() : this.pause();
      },
      { threshold: [0, 0.2, 0.5] }
    );
    this.visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    this.pause();
    window.clearTimeout(this.resumeTimer);
    window.cancelAnimationFrame(this.scrollFrame);
    this.visibilityObserver?.disconnect();
    this.removeEventListener('pointerenter', this.handlePointerEnter);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.track?.removeEventListener('pointerup', this.handlePointerUp);
    this.track?.removeEventListener('pointercancel', this.handlePointerUp);
    this.track?.removeEventListener('scroll', this.handleTrackScroll);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  handleVisibility = () => {
    if (document.hidden) this.pause();
    else this.play();
  };

  goTo(nextIndex) {
    if (!this.track || !this.slides.length) return;

    this.index = (nextIndex + this.slides.length) % this.slides.length;
    const slide = this.slides[this.index];
    const trackBox = this.track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    const left = this.track.scrollLeft + slideBox.left - trackBox.left;

    // Horizontal-only movement prevents autoplay from moving the page vertically.
    this.track.scrollTo({
      left,
      top: 0,
      behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
    });
  }

  updateIndexFromScroll() {
    if (!this.track || !this.slides.length) return;

    const trackLeft = this.track.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.index = closestIndex;
  }

  resumeAfterInteraction() {
    window.clearTimeout(this.resumeTimer);
    this.resumeTimer = window.setTimeout(() => this.play(), 4000);
  }

  pause() {
    window.clearInterval(this.timer);
  }

  play() {
    this.pause();
    if (
      !this.isVisible ||
      !this.ready ||
      this.reduceMotion.matches ||
      document.hidden ||
      this.slides.length < 2
    ) {
      return;
    }

    this.timer = window.setInterval(() => this.goTo(this.index + 1), this.delay);
  }
}

if (!customElements.get('koketa-slider')) customElements.define('koketa-slider', KoketaSlider);
